import { Component, OnInit, OnDestroy, EventEmitter } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import {
  type CabHireEnterpriseSearchInterface,
  UserServices,
  ServiceProvider
} from '@sabstravtech/obtservices/base';
import {
  type CabHireLocationInput,
  type CabHireQuote,
  EnterpriseBasketService,
  EnterpriseSearchService,
  UserService,
  type CabHireResult,
  AddressType,
  ServiceType,
  ChatbotTaxiSearchService
} from '@sabstravtech/obtservices/angular';
import { TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';

import { Geolocation } from '../../../core/classes/geolocation';
import { resultIcons } from '../../../core/enum/result-icons.enum';
import { SortMethod, SortType } from '../../interfaces/sort-type.interface';
import { ThemeService } from '../../../core/services/theme.service';
import { BaseResultsComponent } from '../../../core/components/base_components/base-results.component';

@Component({
  selector: 'app-taxis',
  templateUrl: './taxis.component.html',
  styleUrls: ['./taxis.component.scss'],
  standalone: false
})
export class TaxisComponent extends BaseResultsComponent implements OnInit, OnDestroy {
  cabIconUrl = 'https://images.sabscorp.com/images/TourOp/png/cmac_small.png';
  // readonly cabIconUrl = 'https://images.sabscorp.com/images/TourOp/svg/CAB.svg';

  mapView = false;
  loadingMsg: string;
  selectedItem: CabHireResult;
  pins: (Geolocation & { message: string })[];
  geolocation: Geolocation;
  itemSelected: EventEmitter<CabHireResult> = new EventEmitter();
  resultItemType = resultIcons;
  public taxiServiceType = ServiceType.Cab;
  private lastFocusedTaxiIndex: number | null = null;

  // Parallel array of precomputed taxi image URLs (same length as data.results)
  taxiImageUrls: string[] = [];

  sortingTypes: SortType[] = [
    {
      title: 'Price',
      method: SortMethod.sortByPrice,
      icon: 'ico-dollar',
      dir: true
    }
  ];
  searchParams: CabHireEnterpriseSearchInterface;
  displayItinerary = false;

  constructor(
    public searchService: EnterpriseSearchService,
    private route: ActivatedRoute,
    private basketService: EnterpriseBasketService,
    userService: UserService,
    translateService: TranslateService,
    titleService: Title,
    themeService: ThemeService,
    router: Router,
    public chatbotTaxiSearchService: ChatbotTaxiSearchService
  ) {
    super(titleService, translateService, themeService, searchService, userService, router);
    this.searchParams = this.searchService.searches[ServiceType.Cab];
  }

  get vias(): CabHireLocationInput[] {
    return this.searchParams?.vias;
  }

  get filteredResults$(): Observable<CabHireQuote[]> {
    return this.searchParams.results.pipe(
      map((results: CabHireQuote[]) => {
        const filterId = this.chatbotTaxiSearchService.chatbotFilterId;
        if (!filterId) return results;
        return results.filter((r: CabHireQuote) => r.id === filterId);
      })
    );
  }

  ngOnInit() {
    this.setResultsTitle('Taxi results');
    this.initBaseResults();

    this.subscribe(this.route.queryParams, ({ center }) => {
      this.mapView = !!center;
    });

    this.subscribe(this.searchParams.isLoading, (isLoading: boolean) => {
      if (!isLoading) {
        this.searchParams.sortBy(false);
      }
    });

    // Precompute taxi URLs when results change
    this.subscribe(this.searchParams.results, (results: CabHireQuote[]) => {
      if (results?.length) {
        this.taxiImageUrls = results.map((item) => this.computeTaxiImageUrl(item));
      }
    });

    this.displayItinerary = this.baseUserService.isBasketReplacementUserFavouriteSet();
  }

  ngOnDestroy(): void {
    this.chatbotTaxiSearchService.chatbotFilterId = null;
    super.ngOnDestroy();
  }

  addToCart(item: CabHireQuote): void {
    this.searchService.searches[ServiceType.Cab].addCabHireToBasket(
      item,
      this.searchService.searches[ServiceType.Cab].getSearchQuery()
    );
    this.basketService.toggleMenu();
  }

  goToMapView(taxi: any, itemIndex: number): void {
    this.lastFocusedTaxiIndex = itemIndex;

    this.geolocation = new Geolocation(
      `${taxi.fromLocation.latitude},${taxi.fromLocation.longitude}`
    );

    this.pins = taxi.vias.map((via) => ({
      ...new Geolocation(`${via?.latitude},${via?.longitude}`),
      message: ''
    }));

    this.pins.push({
      ...new Geolocation(`${taxi.toLocation.latitude},${taxi.toLocation.longitude}`),
      message: ''
    });
    this.pins = this.pins.filter((pin) => !Number.isNaN(pin.lat) && !Number.isNaN(pin.lng));
    this.selectedItem = taxi;
    this.cabIconUrl = this.getLogo(taxi);
    this.itemSelected.emit(taxi);
    this.mapView = true;
  }

  onCloseMapView(): void {
    this.mapView = false;
    if (this.lastFocusedTaxiIndex !== null && this.lastFocusedTaxiIndex !== undefined) {
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        const button = document.getElementById(`taxi-map-btn-${this.lastFocusedTaxiIndex}`);
        if (button) {
          button.focus();
          clearInterval(interval);
          this.lastFocusedTaxiIndex = null;
        } else if (attempts > 15) {
          clearInterval(interval);
          this.lastFocusedTaxiIndex = null;
        }
      }, 50);
    }
  }

  skipToResultContent(): boolean {
    return super.skipToResultContent('taxi');
  }

  /**
   * Compute initial taxi image URL with 3-level fallback:
   * 1. Multimedia image (provider branding photo)
   * 2. Supplier name image (logo)
   * 3. Provider icon (CMAC, Groundspan, GRS) - handled in error handler
   */
  private computeTaxiImageUrl(item: CabHireQuote): string {
    // Try multimedia image first
    if (item.additional?.providersPricing?.multiMedias?.multiMedia[1]?.mediaURL) {
      return item.additional.providersPricing.multiMedias.multiMedia[1].mediaURL;
    }
    // Fall back to supplier name image (logo)
    return `https://images.sabscorp.com/images/TourOp/png/${item.supplierName}.png`;
  }

  /**
   * Handle taxi image load errors with cascading fallback:
   * 1. If multimedia failed, try supplier name image
   * 2. If that fails, try provider icon (CMAC, Groundspan, GRS)
   * 3. If all fail, hide the image
   *
   * Updates alt text at each fallback level to match current image source.
   */
  handleTaxiImageError(event: Event, item: CabHireQuote): void {
    const img = event.target as HTMLImageElement;
    const currentSrc = img.src;
    const multimediaUrl = item.additional?.providersPricing?.multiMedias?.multiMedia[1]?.mediaURL;
    const supplierLogoUrl = `https://images.sabscorp.com/images/TourOp/png/${item.supplierName}.png`;
    const providerIconUrl = this.getLogo(item) || this.cabIconUrl;

    // Cascade through fallbacks, updating alt text to match current image source
    if (multimediaUrl && currentSrc === multimediaUrl) {
      // Multimedia failed, try supplier logo
      img.src = supplierLogoUrl;
      img.alt = item.supplierName || 'Taxi provider';
    } else if (currentSrc === supplierLogoUrl) {
      // Supplier logo failed, try provider icon with provider name as alt
      img.src = providerIconUrl;
      img.alt = this.resolveCabIconAlt(providerIconUrl);
    } else {
      // All failed, hide image
      img.style.display = 'none';
    }
  }

  getLogo(item: CabHireQuote): string {
    switch (item.source) {
      case ServiceProvider.CMAC:
        return 'https://images.sabscorp.com/images/TourOp/png/cmac_small.png';
      case ServiceProvider.Groundspan:
        return 'https://images.sabscorp.com/images/TourOp/groundspan-small.jpg';
      case ServiceProvider.Groundscope:
        return 'https://images.sabscorp.com/images/TourOp/GRS.gif';
    }

    return '';
  }

  private resolveCabIconAlt(iconUrl: string): string {
    switch (iconUrl) {
      case 'https://images.sabscorp.com/images/TourOp/png/cmac_small.png':
        return 'CMAC';
      case 'https://images.sabscorp.com/images/TourOp/groundspan-small.jpg':
        return 'Groundspan';
      case 'https://images.sabscorp.com/images/TourOp/GRS.gif':
        return 'Groundscope';
      default:
        return 'Taxi provider logo';
    }
  }

  onTaxiImageError(event: Event, item: CabHireQuote): void {
    const img = event.target as HTMLImageElement;
    const fallback = this.getLogo(item) || this.cabIconUrl;
    if (fallback && img.src !== fallback) {
      img.src = fallback;
    } else {
      img.style.display = 'none';
    }
  }
}
